"use client"

import { Button } from "@/components/ui/Button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type { FragmentProjectTeaserFragment } from "@/lib/gql/solutions-graphql"
import { toAbsoluteUrl } from "@/utils/imageUtils"
import { ArrowRight, Search } from "lucide-react"
import Image from "next/image"
import { useMemo, useState } from "react"

interface ProjectsPageClientProps {
  projects: FragmentProjectTeaserFragment[]
}

function formatProjectDate(timestamp?: any): string {
  if (!timestamp) return ""
  try {
    const date = new Date(timestamp)
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  } catch {
    return ""
  }
}

export default function ProjectsPageClient({
  projects: initialProjects,
}: ProjectsPageClientProps) {
  const [searchQuery, setSearchQuery] = useState("")

  // Client-side filtering
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return initialProjects

    const query = searchQuery.toLowerCase()
    return initialProjects.filter((project) => {
      const title = (project.projectName || project.title || "").toLowerCase()
      const description =
        project.projectDescription?.value?.toLowerCase() ||
        project.projectDescription?.processed?.toLowerCase() ||
        project.body?.summary?.toLowerCase() ||
        ""

      return title.includes(query) || description.includes(query)
    })
  }, [initialProjects, searchQuery])

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search projects..."
          className="pl-10"
          aria-label="Search projects"
        />
      </div>

      {/* Results Count */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {filteredProjects.length} project
          {filteredProjects.length !== 1 ? "s" : ""}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>
      )}

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const projectName = project.projectName || project.title || ""
            const description =
              project.projectDescription?.processed?.replace(/<[^>]*>/g, "") ||
              project.projectDescription?.value?.replace(/<[^>]*>/g, "") ||
              project.body?.summary?.replace(/<[^>]*>/g, "") ||
              ""
            // Extract image URL from HTML or use raw value
            const imageUrlRaw =
              project.projectImage?.processed || project.projectImage?.value
            let imageUrl: string | null = null

            if (imageUrlRaw && imageUrlRaw.trim()) {
              // If it contains HTML img tag, extract src attribute
              if (imageUrlRaw.includes("<img")) {
                const srcMatch = imageUrlRaw.match(/src=["']([^"']+)["']/i)
                if (srcMatch && srcMatch[1] && srcMatch[1].trim()) {
                  const extractedUrl = srcMatch[1].trim()
                  // If extracted URL is absolute, use it directly
                  if (/^https?:\/\//i.test(extractedUrl)) {
                    imageUrl = extractedUrl
                  } else {
                    // Otherwise, convert to absolute using backend
                    const convertedUrl = toAbsoluteUrl(extractedUrl, true)
                    imageUrl =
                      convertedUrl && convertedUrl.trim() ? convertedUrl : null
                  }
                }
              }
              // If it's already an absolute URL, use it directly
              else if (/^https?:\/\//i.test(imageUrlRaw)) {
                imageUrl = imageUrlRaw.trim()
              }
              // Otherwise, treat as relative path and convert to absolute
              else {
                const convertedUrl = toAbsoluteUrl(imageUrlRaw, true)
                imageUrl =
                  convertedUrl && convertedUrl.trim() ? convertedUrl : null
              }
            }
            const projectLink = project.projectLink?.url || ""
            const linkUrl = projectLink.replace(/^internal:/, "") || ""
            const isExternal = projectLink.startsWith("http")
            const projectDate = formatProjectDate(
              project.projectDate?.timestamp
            )

            return (
              <Card
                key={project.id}
                className="h-full flex flex-col hover:shadow-md transition-shadow"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  {imageUrl && imageUrl.trim() ? (
                    <div className="mb-4 flex items-center justify-center h-24 relative w-full">
                      <Image
                        src={imageUrl}
                        alt={projectName}
                        fill
                        className="object-contain"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 320px"
                        quality={70}
                      />
                    </div>
                  ) : (
                    <div className="mb-4 flex items-center justify-center h-24 relative w-full">
                      <span className="text-muted-foreground text-sm sr-only">
                        No image
                      </span>
                    </div>
                  )}
                  <h3 className="text-base font-semibold mb-2">
                    {projectName}
                  </h3>

                  {description && (
                    <p className="text-muted-foreground text-sm leading-relaxed flex-1 mb-4">
                      {description.trim().substring(0, 150)}
                      {description.length > 150 && "..."}
                    </p>
                  )}

                  {linkUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full sm:w-auto mt-auto"
                      aria-label={`Read more about ${projectName}`}
                      onClick={() =>
                        window.open(linkUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      Read more
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {searchQuery
              ? "No projects found matching your search."
              : "No projects found."}
          </p>
        </div>
      )}
    </div>
  )
}
